import { Link } from "react-router-dom";
import { CalendarClock, Wallet } from "lucide-react";
import { stepsFor } from "../lib/content";
import { useSite } from "../context/SiteContext";
import { PageHero } from "../components/site/Layout";
import { SectionTitle } from "../components/site/Decor";

export default function Ordering() {
  const { site } = useSite();
  const PAYMENTS = site.payments;
  const STEPS = stepsFor(PAYMENTS);
  return (
    <>
      <PageHero eyebrow="Ordering & payment" title="How it" script="works" testid="ordering-hero">
        Two easy ways to order: pick a set-price item from the Shop, or request a quote for something one-of-a-kind.
      </PageHero>
      <section className="mx-auto grid max-w-7xl gap-6 px-6 pt-16 md:grid-cols-2" data-testid="ordering-paths">
        <div className="rounded-3xl bg-[#0E0B0D] p-8 text-white" data-testid="ordering-path-shop">
          <p className="eyebrow">Set prices</p>
          <h2 className="mt-2 font-display text-2xl font-bold">Shop ready-to-order items</h2>
          <p className="mt-3 text-white/75">Prices are listed by us. Pick your color, size and design, see your total right away and pay securely online by card or Apple Pay.</p>
          <Link to="/shop" className="btn-pink mt-6" data-testid="ordering-shop-btn">Go to Shop</Link>
        </div>
        <div className="gold-trim rounded-3xl p-8" data-testid="ordering-path-custom">
          <p className="eyebrow">Custom orders</p>
          <h2 className="mt-2 font-display text-2xl font-bold">Request a quote</h2>
          <p className="mt-3 text-black/70">For one-of-a-kind pieces and bulk orders, tell us your idea. We'll send a quote with a secure Pay Now link, or you can pay with {site.payments.join(", ")}.</p>
          <Link to="/order" className="btn-outline-gold mt-6" data-testid="ordering-quote-btn">Request a Quote</Link>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 py-20">
        <p className="mb-6 font-display text-xl font-bold">How custom orders work</p>
        <ol className="grid gap-6 md:grid-cols-5" data-testid="ordering-steps">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative rounded-3xl bg-[#FFF4F8] p-6" data-testid={`ordering-step-${i + 1}`}>
              <span className="font-display text-5xl font-black text-[#FF1F8E]">{i + 1}</span>
              <h3 className="mt-3 font-display text-xl font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-[#0E0B0D]/70">{s.text}</p>
              {i < STEPS.length - 1 && <div className="stitch-line absolute -right-6 top-12 hidden w-6 md:block" />}
            </li>
          ))}
        </ol>
      </section>
      <section className="bg-[#0E0B0D] text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2">
          <div data-testid="ordering-policy">
            <SectionTitle eyebrow="Good to know" title="Order" script="policy" light />
            <div className="mt-8 space-y-5">
              <div className="flex gap-4"><CalendarClock className="mt-1 h-6 w-6 shrink-0 text-[#D4A537]" /><p className="text-white/80"><b className="text-white">Turnaround:</b> {site.ordering_turnaround}</p></div>
              <div className="flex gap-4"><Wallet className="mt-1 h-6 w-6 shrink-0 text-[#D4A537]" /><p className="text-white/80"><b className="text-white">Payment:</b> {site.ordering_payment}</p></div>
            </div>
          </div>
          <div data-testid="ordering-payments">
            <SectionTitle eyebrow="We accept" title="Payment" script="methods" light />
            <div className="mt-8 grid grid-cols-2 gap-4">
              {PAYMENTS.map((p) => (
                <div key={p} className={`gold-trim rounded-2xl px-5 py-6 text-center font-display text-lg font-bold ${PAYMENTS.length % 2 ? "last:col-span-2" : ""}`} data-testid={`payment-method-${p.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>{p}</div>
              ))}
            </div>
            <p className="mt-5 text-sm text-white/55">Payments are arranged directly with us after your quote. Bank transfer details are shared privately with your quote and never posted online. No payment is taken on this website.</p>
          </div>
        </div>
      </section>
      <section className="px-6 py-16 text-center">
        <Link to="/order" className="btn-pink" data-testid="ordering-start-order-btn">Start a Custom Order</Link>
      </section>
    </>
  );
}
